// 서브페이지 본문 데이터 (인재채용 · 자료실 · 개인정보처리방침 · 이용약관)
// 복지 내용 출처: 회사소개자료 "복지 및 보안", "인재 육성 및 복지"
import type { Lang } from "@/lib/useLang";

export type Section = { heading: string; paragraphs?: string[]; bullets?: string[] };

export const CAREERS: Record<
  Lang,
  {
    intro: string;
    position: { title: string; team: string; location: string; type: string };
    duties: string[];
    requirements: string[];
    preferred: string[];
    benefits: { icon: string; title: string; desc: string }[];
    apply: { title: string; steps: string[]; email: string; subject: string };
  }
> = {
  ko: {
    intro:
      "오션커넥트는 접수부터 근본원인 해결까지 4단계 격상 체계로 고객 선단을 지원합니다. 책임감 있는 밀착 지원을 함께 만들어갈 선박 IT 엔지니어를 상시 모집합니다.",
    position: { title: "선박 IT 엔지니어", team: "기술지원팀", location: "부산 본사 (국내 항만 · 해외 출장)", type: "정규직 · 상시 채용" },
    duties: [
      "선박 IT 통합 유지보수 — 방선 점검·수리, 원격 지원, 장애 대응",
      "선박 유무선 네트워크 설계·구축 및 방화벽 정책 관리",
      "위성통신(VSAT·FBB·STARLINK) 및 보안 장비 설치·커미셔닝",
      "신조·인수 선박 IT 시스템 구축, 서비스 이력 기록 및 보고서 작성",
    ],
    requirements: [
      "PC·서버·네트워크 장비에 대한 기본 이해와 문제 해결 능력",
      "국내 항만 방선 및 해외 출장이 가능한 분",
      "책임감 있게 고객과 소통할 수 있는 분",
    ],
    preferred: [
      "선박·해운 IT 또는 통신 장비 관련 경력",
      "네트워크·정보보안·정보처리 관련 자격증",
      "영어 커뮤니케이션 가능자 (외국 선원·선사 대응)",
    ],
    benefits: [
      { icon: "ri-time-line", title: "유연 근무", desc: "현장 파견 근로자를 위한 유연근무제 시행" },
      { icon: "ri-money-dollar-circle-line", title: "출장 · 주말 수당", desc: "주말 및 해외 출장 시 별도 수당 지급" },
      { icon: "ri-book-open-line", title: "교육비 지원", desc: "기술 성장을 위한 사외 교육비 지원" },
      { icon: "ri-award-line", title: "자격 취득 지원", desc: "업무 관련 자격증 시험 비용 지원" },
      { icon: "ri-car-line", title: "교통비", desc: "전체 직원 출퇴근 유류비 지원" },
      { icon: "ri-safe-2-line", title: "내일채움공제", desc: "장기 근속을 위한 내일채움공제 가입" },
      { icon: "ri-shield-check-line", title: "안전 · 보안 교육", desc: "선박 환경 맞춤 안전 교육, 분기별 보안 교육" },
    ],
    apply: {
      title: "지원 방법",
      steps: ["이력서(경력기술서 포함)를 이메일로 보내주세요.", "서류 검토 후 면접 일정을 개별 연락드립니다.", "면접 → 처우 협의 → 입사"],
      email: "info@oceanconnect.co.kr",
      subject: "[채용지원] 선박 IT 엔지니어 - 성명",
    },
  },
  en: {
    intro:
      "Ocean Connect supports fleets through a four-level escalation path from intake to root-cause resolution. We are always looking for vessel IT engineers who want to deliver responsive, hands-on support.",
    position: { title: "Vessel IT Engineer", team: "Technical Support", location: "Busan HQ (travel to Korean ports and overseas)", type: "Full-time · Open recruitment" },
    duties: [
      "Integrated vessel IT maintenance — onboard inspection and repair, remote support, fault response",
      "Vessel wired/wireless network design, deployment, and firewall policy management",
      "Installation and commissioning of satellite (VSAT · FBB · STARLINK) and security equipment",
      "IT setup for newbuild and takeover vessels, service logging and reporting",
    ],
    requirements: [
      "Working knowledge of PCs, servers, and network equipment with troubleshooting skills",
      "Able to board vessels at Korean ports and travel overseas",
      "Responsible communicator with clients and crew",
    ],
    preferred: [
      "Experience in vessel/maritime IT or communication equipment",
      "Network, information security, or IT certifications",
      "English communication skills (foreign crew and shipping companies)",
    ],
    benefits: [
      { icon: "ri-time-line", title: "Flexible Work", desc: "Flexible hours for field-deployed staff" },
      { icon: "ri-money-dollar-circle-line", title: "Travel & Weekend Pay", desc: "Extra allowances for weekend and overseas work" },
      { icon: "ri-book-open-line", title: "Training Support", desc: "External training costs covered for technical growth" },
      { icon: "ri-award-line", title: "Certification Support", desc: "Exam fees covered for job-related certifications" },
      { icon: "ri-car-line", title: "Commuting", desc: "Fuel allowance for all employees" },
      { icon: "ri-safe-2-line", title: "Retention Savings Plan", desc: "Government-backed long-term savings program" },
      { icon: "ri-shield-check-line", title: "Safety & Security Training", desc: "Vessel-specific safety training and quarterly security training" },
    ],
    apply: {
      title: "How to Apply",
      steps: ["Email your résumé (with career details).", "We will contact you individually to schedule an interview.", "Interview → offer → onboarding"],
      email: "info@oceanconnect.co.kr",
      subject: "[Application] Vessel IT Engineer - Your Name",
    },
  },
};

export const DOWNLOADS: Record<
  Lang,
  { intro: string; items: { icon: string; title: string; desc: string }[]; request: { title: string; desc: string; email: string; subject: string; button: string } }
> = {
  ko: {
    intro: "회사소개서와 솔루션 자료는 요청하시는 분께 이메일로 보내드립니다. 아래 자료 중 필요한 항목과 소속·연락처를 적어 요청해 주세요.",
    items: [
      { icon: "ri-file-text-line", title: "회사소개서 (국문)", desc: "기업 개요, 조직 역량, 핵심 서비스, 주력 솔루션 및 공급 기자재" },
      { icon: "ri-shield-keyhole-line", title: "사이버보안 솔루션 자료", desc: "방화벽(UTM), 엔드포인트 보안, USB 보안 구성과 적용 사례" },
      { icon: "ri-remote-control-line", title: "원격관리(RMM) 서비스 안내", desc: "원격 접속 방식, 지원 범위, 운영 프로세스" },
      { icon: "ri-alarm-warning-line", title: "CITADEL 비상통신 · 기자재 카탈로그", desc: "CITADEL, CCTV, 디지털경사계, 방수 모니터, VHF 녹음장치, GPS 리시버" },
    ],
    request: {
      title: "자료 요청",
      desc: "이메일로 요청하시면 담당자가 확인 후 보내드립니다. 온라인 문의 폼의 '기타 문의'로 요청하셔도 됩니다.",
      email: "info@oceanconnect.co.kr",
      subject: "[자료요청] 회사소개서 / 솔루션 자료",
      button: "이메일로 자료 요청",
    },
  },
  en: {
    intro: "Our company profile and solution materials are sent by email on request. Please tell us which items you need along with your organization and contact details.",
    items: [
      { icon: "ri-file-text-line", title: "Company Profile", desc: "Company overview, organizational capability, core services, key solutions and equipment" },
      { icon: "ri-shield-keyhole-line", title: "Cyber Security Solutions", desc: "Firewall (UTM), endpoint security, and USB security configuration and use cases" },
      { icon: "ri-remote-control-line", title: "Remote Management (RMM) Guide", desc: "Remote access method, support scope, and operating process" },
      { icon: "ri-alarm-warning-line", title: "CITADEL & Equipment Catalog", desc: "CITADEL, CCTV, digital inclinometer, waterproof monitor, VHF recorder, GPS receiver" },
    ],
    request: {
      title: "Request Materials",
      desc: "Email us and our team will send the materials after review. You may also use the online inquiry form ('Other Inquiries').",
      email: "info@oceanconnect.co.kr",
      subject: "[Request] Company profile / solution materials",
      button: "Request by Email",
    },
  },
};

export const PRIVACY: Record<Lang, { effective: string; intro: string; sections: Section[] }> = {
  ko: {
    effective: "2026년 10월 1일",
    intro:
      "주식회사 오션커넥트(이하 '회사')는 「개인정보 보호법」 등 관련 법령을 준수하며, 홈페이지 이용자의 개인정보를 보호하기 위해 다음과 같이 개인정보처리방침을 수립·공개합니다.",
    sections: [
      {
        heading: "1. 수집하는 개인정보 항목 및 수집 방법",
        paragraphs: ["회사는 홈페이지 온라인 문의 접수를 위해 아래 항목을 이용자가 직접 입력하는 방식으로 수집합니다."],
        bullets: ["필수 항목: 이름, 이메일, 문의 유형, 문의 내용", "선택 항목: 회사/소속, 연락처", "서비스 이용 과정에서 접속 IP, 브라우저 정보 등이 자동으로 생성·수집될 수 있습니다."],
      },
      {
        heading: "1-1. 웹사이트 방문 통계(쿠키) 이용",
        paragraphs: ["회사는 홈페이지 이용 현황 분석을 위해 Google LLC 의 Google Analytics 를 사용합니다. Google Analytics 는 쿠키를 통해 방문 페이지, 체류 시간, 유입 경로, 브라우저·기기 정보 등을 수집하며, IP 주소는 익명화되어 처리됩니다. 수집된 정보는 개인을 식별하는 데 사용되지 않습니다. 이용자는 브라우저 설정에서 쿠키 저장을 거부하거나 Google 의 차단 도구(https://tools.google.com/dlpage/gaoptout)를 통해 수집을 거부할 수 있습니다."],
      },
      {
        heading: "2. 개인정보의 수집 및 이용 목적",
        bullets: ["문의 사항 접수, 확인 및 답변", "서비스 제안, 견적 및 기술 상담 안내", "문의 처리 이력 관리"],
      },
      {
        heading: "3. 개인정보의 보유 및 이용 기간",
        paragraphs: ["수집된 개인정보는 문의 처리 완료 후 1년간 보관한 뒤 지체 없이 파기합니다. 다만 관련 법령에 따라 보존할 필요가 있는 경우에는 해당 법령에서 정한 기간 동안 보관합니다."],
      },
      {
        heading: "4. 개인정보의 제3자 제공",
        paragraphs: ["회사는 이용자의 개인정보를 제2조의 목적 범위를 넘어 제3자에게 제공하지 않습니다. 다만 이용자가 사전에 동의하거나 법령의 규정에 의한 경우는 예외로 합니다."],
      },
      {
        heading: "5. 개인정보 처리의 위탁",
        paragraphs: ["회사는 온라인 문의 폼의 전송·접수 처리를 위해 홈페이지 폼 처리 서비스를 이용할 수 있으며, 위탁 시 관련 법령에 따라 수탁자가 개인정보를 안전하게 처리하도록 관리·감독합니다."],
      },
      {
        heading: "6. 정보주체의 권리 · 의무 및 행사 방법",
        paragraphs: ["이용자는 언제든지 자신의 개인정보에 대한 열람, 정정, 삭제, 처리정지를 요구할 수 있으며, 회사는 지체 없이 조치합니다. 요구는 아래 개인정보 보호책임자에게 이메일 또는 전화로 하실 수 있습니다."],
      },
      {
        heading: "7. 개인정보의 파기 절차 및 방법",
        paragraphs: ["보유 기간이 경과하거나 처리 목적이 달성된 개인정보는 지체 없이 파기합니다. 전자적 파일 형태의 정보는 복구할 수 없는 기술적 방법으로 삭제하며, 종이 문서는 분쇄 또는 소각합니다."],
      },
      {
        heading: "8. 개인정보의 안전성 확보 조치",
        bullets: ["개인정보 취급 담당자 최소화 및 접근 권한 관리", "암호화된 통신(HTTPS)을 통한 전송", "정기적인 보안 교육 실시"],
      },
      {
        heading: "9. 개인정보 보호책임자",
        bullets: ["성명: 이준원 (대표이사)", "전화: 051-715-0611", "이메일: info@oceanconnect.co.kr", "주소: 부산광역시 남구 신선로 365, 용당9관 218호"],
      },
      {
        heading: "10. 개인정보처리방침의 변경",
        paragraphs: ["이 개인정보처리방침은 시행일로부터 적용되며, 법령·정책 또는 보안 기술의 변경에 따라 내용이 추가·삭제·수정될 경우 홈페이지를 통해 공지합니다."],
      },
    ],
  },
  en: {
    effective: "October 1, 2026",
    intro:
      "Ocean Connect Co., Ltd. (the 'Company') complies with the Personal Information Protection Act of Korea and related laws, and publishes this policy to protect the personal information of website users.",
    sections: [
      {
        heading: "1. Information Collected and Method",
        paragraphs: ["The Company collects the following information entered directly by users through the online inquiry form."],
        bullets: ["Required: name, email, inquiry type, message", "Optional: company/organization, phone number", "Access IP and browser information may be generated automatically during use."],
      },
      {
        heading: "1-1. Website Analytics (Cookies)",
        paragraphs: ["The Company uses Google Analytics by Google LLC to analyze website usage. Google Analytics uses cookies to collect pages visited, time on site, referral source and browser/device information; IP addresses are anonymized. The data is not used to identify individuals. You may refuse cookies in your browser settings or opt out via Google's add-on (https://tools.google.com/dlpage/gaoptout)."],
      },
      { heading: "2. Purpose of Collection and Use", bullets: ["Receiving, reviewing, and answering inquiries", "Service proposals, quotations, and technical consultation", "Managing inquiry records"] },
      { heading: "3. Retention Period", paragraphs: ["Personal information is kept for one year after the inquiry is resolved and then destroyed without delay, unless a longer period is required by law."] },
      { heading: "4. Provision to Third Parties", paragraphs: ["The Company does not provide personal information to third parties beyond the purposes above, except with prior consent or as required by law."] },
      { heading: "5. Outsourcing", paragraphs: ["The Company may use a form-processing service to deliver online inquiries and supervises such providers to process personal information safely in accordance with the law."] },
      { heading: "6. Rights of Data Subjects", paragraphs: ["Users may request access, correction, deletion, or suspension of processing of their personal information at any time by contacting the privacy officer below. The Company will act without delay."] },
      { heading: "7. Destruction", paragraphs: ["Information whose retention period has expired or purpose has been achieved is destroyed without delay — electronic files by irrecoverable technical means and paper documents by shredding or incineration."] },
      { heading: "8. Security Measures", bullets: ["Minimized staff access and access-right management", "Encrypted transmission (HTTPS)", "Regular security training"] },
      { heading: "9. Privacy Officer", bullets: ["Name: Lee Jun-won (CEO)", "Tel: +82-51-715-0611", "Email: info@oceanconnect.co.kr", "Address: Room 218, Yongdang Building 9, 365 Sinseon-ro, Nam-gu, Busan, Korea"] },
      { heading: "10. Changes to This Policy", paragraphs: ["This policy applies from the effective date. Changes due to laws, policies, or security technology will be announced on the website."] },
    ],
  },
};

export const TERMS: Record<Lang, { effective: string; sections: Section[] }> = {
  ko: {
    effective: "2026년 10월 1일",
    sections: [
      { heading: "제1조 (목적)", paragraphs: ["이 약관은 주식회사 오션커넥트(이하 '회사')가 운영하는 홈페이지(이하 '사이트')의 이용 조건 및 절차, 회사와 이용자의 권리·의무를 규정함을 목적으로 합니다."] },
      { heading: "제2조 (서비스의 내용)", paragraphs: ["사이트는 회사의 사업 영역, 솔루션, 실적, 소식 등 정보를 제공하고 온라인 문의를 접수합니다. 회사는 사이트의 내용을 사전 통지 없이 변경할 수 있습니다."] },
      { heading: "제3조 (저작권 및 이용 제한)", paragraphs: ["사이트에 게시된 문서, 이미지, 디자인 등 모든 콘텐츠의 저작권은 회사에 있습니다. 이용자는 회사의 사전 서면 동의 없이 콘텐츠를 복제, 배포, 전송, 출판하거나 상업적으로 이용할 수 없습니다."] },
      { heading: "제4조 (이용자의 의무)", bullets: ["허위 정보를 입력하거나 타인의 정보를 도용하는 행위 금지", "사이트의 정상적인 운영을 방해하는 행위 금지", "관련 법령 및 이 약관에서 금지하는 행위 금지"] },
      { heading: "제5조 (면책)", paragraphs: ["회사는 사이트에 게시된 정보의 정확성을 위해 노력하나, 정보의 오류·누락 또는 이용으로 인해 발생한 손해에 대하여 법령에서 정한 경우를 제외하고 책임을 지지 않습니다. 사이트에서 링크된 외부 사이트의 내용에 대해서는 책임을 지지 않습니다."] },
      { heading: "제6조 (개인정보 보호)", paragraphs: ["회사는 이용자의 개인정보를 관련 법령 및 개인정보처리방침에 따라 보호합니다."] },
      { heading: "제7조 (준거법 및 관할)", paragraphs: ["이 약관은 대한민국 법률에 따라 해석되며, 사이트 이용과 관련한 분쟁은 회사 본사 소재지를 관할하는 법원을 제1심 관할 법원으로 합니다."] },
    ],
  },
  en: {
    effective: "October 1, 2026",
    sections: [
      { heading: "Article 1 (Purpose)", paragraphs: ["These terms govern the use of the website operated by Ocean Connect Co., Ltd. (the 'Company') and the rights and obligations of the Company and users."] },
      { heading: "Article 2 (Services)", paragraphs: ["The website provides information on the Company's business, solutions, track record, and news, and receives online inquiries. The Company may change the content without prior notice."] },
      { heading: "Article 3 (Copyright)", paragraphs: ["All content on the website, including text, images, and design, is copyrighted by the Company and may not be reproduced, distributed, transmitted, published, or used commercially without prior written consent."] },
      { heading: "Article 4 (User Obligations)", bullets: ["No false information or misuse of others' information", "No interference with normal operation of the website", "No acts prohibited by law or these terms"] },
      { heading: "Article 5 (Disclaimer)", paragraphs: ["The Company strives for accuracy but is not liable for damages arising from errors, omissions, or use of the information except as required by law, nor for the content of external linked sites."] },
      { heading: "Article 6 (Privacy)", paragraphs: ["The Company protects users' personal information in accordance with applicable laws and its Privacy Policy."] },
      { heading: "Article 7 (Governing Law)", paragraphs: ["These terms are governed by the laws of the Republic of Korea. Disputes are subject to the court having jurisdiction over the Company's head office."] },
    ],
  },
};
